import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { toast } from "sonner";
import { CalendarPlus, Loader2, Search } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// Fallback specialites list
const DEFAULT_SPECIALITES = [
  "Toutes", "Burger truck", "Smash burger", "Hot-dog truck", "Fish & chips", "Pizza truck",
  "Thai food", "Poke bowl", "Sushi truck", "Tacos mexicains", "Crêpes", "Gaufres",
  "Desserts", "Café truck", "Autre"
];

export default function ProposeEventDialog({ isOpen, onOpenChange, token, onEventProposed, initialData = null }) {
  const [proposeData, setProposeData] = useState({
    titre: "",
    date_debut: "",
    date_fin: "",
    lieu: "",
    adresse: "",
    information_complementaire: "",
    type_evenement: "collectif",
    nombre_food_trucks_requis: 3,
    specialites_recherchees: [],
    specialite_recherchee_autre: "",
    proposeur_participe: true,
    responsable_planning_id: "",
    crm_client_id: ""
  });

  // Get user role from token
  const [userRole, setUserRole] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUserName, setCurrentUserName] = useState(null);
  
  useEffect(() => {
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserRole(payload.role);
        setCurrentUserId(payload.sub);
        setCurrentUserName(payload.nom || payload.email);
      } catch (e) {
        console.error("Error decoding token:", e);
      }
    }
  }, [token]);

  const isAdminOrBureau = userRole === "admin" || userRole === "bureau";
  const isMembre = userRole === "membre";
  const [submitting, setSubmitting] = useState(false);
  const [specialites, setSpecialites] = useState(DEFAULT_SPECIALITES);
  const [searchSpecialite, setSearchSpecialite] = useState("");
  const [loadingSpecialites, setLoadingSpecialites] = useState(false);
  const [bureauMembers, setBureauMembers] = useState([]);

  useEffect(() => {
    const loadSpecialites = async () => {
      setLoadingSpecialites(true);
      try {
        const response = await fetch(API_URL + "/specialites");
        if (response.ok) {
          const data = await response.json();
          setSpecialites(["Toutes", ...data.specialites, "Autre"]);
        }
      } catch (error) {
        console.error("Error loading specialites:", error);
      } finally {
        setLoadingSpecialites(false);
      }
    };
    loadSpecialites();
  }, []);

  // Load bureau members for responsable selection
  useEffect(() => {
    const loadBureauMembers = async () => {
      try {
        const response = await fetch(API_URL + "/users/bureau-members", {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setBureauMembers(data);
        }
      } catch (error) {
        console.error("Error loading bureau members:", error);
      }
    };
    if (token) loadBureauMembers();
  }, [token]);

  // Pre-fill data from CRM client if provided
  useEffect(() => {
    if (initialData && isOpen) {
      const specialites = [];
      if (initialData.specialites) {
        const specString = Array.isArray(initialData.specialites) 
          ? initialData.specialites.join(", ") 
          : initialData.specialites;
        // Try to match with known specialites
        const specLower = specString.toLowerCase();
        if (specLower.includes("toutes") || specLower.includes("peu importe") || specLower.includes("à voir")) {
          specialites.push("Toutes");
        } else {
          // Add as "Autre" if specific
          specialites.push("Autre");
        }
      }
      
      setProposeData(prev => ({
        ...prev,
        titre: initialData.nom_entreprise ? `Événement ${initialData.nom_entreprise}` : "",
        date_debut: initialData.date_evenement || "",
        date_fin: initialData.date_evenement || "",
        lieu: initialData.ville || "",
        adresse: initialData.adresse || "",
        information_complementaire: [
          initialData.notes || "",
          initialData.nombre_participants ? `Participants: ${initialData.nombre_participants} personnes` : "",
          initialData.budget ? `Budget: ${initialData.budget}` : "",
          initialData.nom_contact ? `Contact: ${initialData.nom_contact}` : "",
          initialData.email ? `Email: ${initialData.email}` : "",
          initialData.telephone ? `Tél: ${initialData.telephone}` : ""
        ].filter(Boolean).join("\n"),
        nombre_food_trucks_requis: initialData.nombre_participants 
          ? Math.max(1, Math.ceil(initialData.nombre_participants / 150)) 
          : 3,
        specialites_recherchees: specialites,
        specialite_recherchee_autre: initialData.specialites && !["toutes", "peu importe", "à voir"].some(s => 
          (Array.isArray(initialData.specialites) ? initialData.specialites.join(" ") : initialData.specialites).toLowerCase().includes(s)
        ) ? (Array.isArray(initialData.specialites) ? initialData.specialites.join(", ") : initialData.specialites) : "",
        crm_client_id: initialData.id || ""
      }));
    }
  }, [initialData, isOpen]);

  const filteredSpecialites = specialites.filter(spec => 
    spec.toLowerCase().includes(searchSpecialite.toLowerCase())
  );

  const handleSpecialiteChange = (specialite, checked) => {
    let newSpecialites = [...proposeData.specialites_recherchees];
    
    if (specialite === "Toutes") {
      if (checked) {
        newSpecialites = ["Toutes"];
      } else {
        newSpecialites = [];
      }
    } else {
      newSpecialites = newSpecialites.filter(s => s !== "Toutes");
      
      if (checked) {
        if (!newSpecialites.includes(specialite)) {
          newSpecialites.push(specialite);
        }
      } else {
        newSpecialites = newSpecialites.filter(s => s !== specialite);
      }
    }
    
    setProposeData({ ...proposeData, specialites_recherchees: newSpecialites });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const response = await fetch(API_URL + "/events/propose", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token
        },
        body: JSON.stringify(proposeData)
      });
      
      if (response.ok) {
        const eventData = await response.json();
        if (isAdminOrBureau) {
          toast.success("Événement créé ! Les demandes de participation ont été envoyées aux food trucks.");
        } else {
          toast.success("Proposition envoyée ! Elle sera examinée par l'administration.");
        }
        setProposeData({
          titre: "",
          date_debut: "",
          date_fin: "",
          lieu: "",
          adresse: "",
          information_complementaire: "",
          type_evenement: "collectif",
          nombre_food_trucks_requis: 3,
          specialites_recherchees: [],
          specialite_recherchee_autre: "",
          proposeur_participe: true
        });
        onOpenChange(false);
        if (onEventProposed) onEventProposed(eventData);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de la proposition");
      }
    } catch (error) {
      toast.error("Erreur de connexion");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button data-testid="propose-event-btn" className="bg-primary hover:bg-primary/90 text-sm sm:text-base">
          <CalendarPlus className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
          Je propose un événement
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="text-secondary text-xl">
            {isAdminOrBureau ? "Créer un événement" : "Proposer un événement"}
          </DialogTitle>
          <DialogDescription>
            {isAdminOrBureau 
              ? "L'événement sera créé et les demandes de participation seront envoyées directement aux food trucks."
              : "Soumettez votre proposition d'événement. Elle sera examinée par l'équipe."
            }
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-secondary font-semibold">Titre de l'evenement *</Label>
              <Input
                value={proposeData.titre}
                onChange={(e) => setProposeData({ ...proposeData, titre: e.target.value })}
                placeholder="Ex: Marche de Noel"
                required
                className="border-[#E5E0DC]"
              />
            </div>
            <div>
              <Label className="text-secondary font-semibold">Lieu *</Label>
              <Input
                value={proposeData.lieu}
                onChange={(e) => setProposeData({ ...proposeData, lieu: e.target.value })}
                placeholder="Ex: Place de la Mairie"
                required
                className="border-[#E5E0DC]"
              />
            </div>
          </div>

          <div>
            <Label className="text-secondary font-semibold">Adresse complete</Label>
            <Input
              value={proposeData.adresse}
              onChange={(e) => setProposeData({ ...proposeData, adresse: e.target.value })}
              placeholder="Ex: 1 Place de la Mairie, 75001 Paris"
              className="border-[#E5E0DC]"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-secondary font-semibold">Date de debut *</Label>
              <Input
                type="date"
                value={proposeData.date_debut}
                onChange={(e) => setProposeData({ ...proposeData, date_debut: e.target.value })}
                required
                className="border-[#E5E0DC]"
              />
            </div>
            <div>
              <Label className="text-secondary font-semibold">Date de fin</Label>
              <Input
                type="date"
                value={proposeData.date_fin}
                onChange={(e) => setProposeData({ ...proposeData, date_fin: e.target.value })}
                className="border-[#E5E0DC]"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-secondary font-semibold">Type d'evenement</Label>
              <select
                value={proposeData.type_evenement}
                onChange={(e) => setProposeData({ ...proposeData, type_evenement: e.target.value })}
                className="w-full p-2 border border-[#E5E0DC] rounded-md bg-white"
              >
                <option value="collectif">Collectif (plusieurs food trucks)</option>
                <option value="prive">Prive / Entreprise</option>
                <option value="festival">Festival</option>
                <option value="marche">Marche</option>
              </select>
            </div>
            <div>
              <Label className="text-secondary font-semibold">Nombre de food trucks souhaites</Label>
              <Input
                type="text"
                inputMode="numeric"
                value={proposeData.nombre_food_trucks_requis}
                onChange={(e) => {
                  const val = e.target.value;
                  setProposeData({ ...proposeData, nombre_food_trucks_requis: val === "" ? 1 : parseInt(val) || 1 });
                }}
                className="border-[#E5E0DC]"
                placeholder="1"
              />
            </div>
          </div>

          <div>
            <Label className="text-secondary font-semibold">Specialites recherchees</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Selectionnez les specialites souhaitees (ou "Toutes" pour inviter tous les food trucks)
            </p>
            
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Rechercher une specialite..."
                value={searchSpecialite}
                onChange={(e) => setSearchSpecialite(e.target.value)}
                className="pl-10 border-[#E5E0DC]"
              />
            </div>
            
            {proposeData.specialites_recherchees.length > 0 && !proposeData.specialites_recherchees.includes("Toutes") && (
              <div className="flex flex-wrap gap-1 mb-2">
                {proposeData.specialites_recherchees.filter(s => s !== "Autre").map(spec => (
                  <span 
                    key={spec}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-primary text-white"
                  >
                    {spec}
                    <button
                      type="button"
                      onClick={() => handleSpecialiteChange(spec, false)}
                      className="ml-1 hover:text-red-200"
                    >
                      x
                    </button>
                  </span>
                ))}
              </div>
            )}
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2 p-3 border border-[#E5E0DC] rounded-md max-h-64 overflow-y-auto bg-[#F9F7F5]">
              {loadingSpecialites ? (
                <div className="col-span-3 flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-primary" />
                  <span className="ml-2 text-sm text-muted-foreground">Chargement...</span>
                </div>
              ) : filteredSpecialites.length === 0 ? (
                <div className="col-span-3 text-center py-4 text-muted-foreground text-sm">
                  Aucune specialite trouvee pour "{searchSpecialite}"
                </div>
              ) : (
                filteredSpecialites.map((spec) => {
                  const isCategory = ["Salé", "Sucré", "Boissons"].includes(spec);
                  return (
                    <div key={spec} className={`flex items-center space-x-2 ${isCategory ? "col-span-2 md:col-span-3 bg-[#E8E3DE] p-2 rounded-md mt-2 -mx-1" : ""}`}>
                      <Checkbox
                        id={"spec-" + spec}
                        checked={proposeData.specialites_recherchees.includes(spec)}
                        onCheckedChange={(checked) => handleSpecialiteChange(spec, checked)}
                        disabled={spec !== "Toutes" && proposeData.specialites_recherchees.includes("Toutes")}
                      />
                      <label 
                        htmlFor={"spec-" + spec} 
                        className={`text-sm cursor-pointer ${spec === "Toutes" ? "font-semibold text-primary" : ""} ${isCategory ? "font-bold text-secondary text-base" : ""}`}
                      >
                        {isCategory ? `🏷️ ${spec}` : spec}
                      </label>
                    </div>
                  );
                })
              )}
            </div>
            {proposeData.specialites_recherchees.includes("Toutes") && (
              <p className="text-xs text-primary mt-1 font-medium">
                Tous les food trucks seront invites a participer
              </p>
            )}
          </div>

          {proposeData.specialites_recherchees.includes("Autre") && (
            <div>
              <Label className="text-secondary font-semibold">Precision pour "Autre"</Label>
              <Input
                value={proposeData.specialite_recherchee_autre}
                onChange={(e) => setProposeData({ ...proposeData, specialite_recherchee_autre: e.target.value })}
                placeholder="Precisez la specialite recherchee..."
                className="border-[#E5E0DC]"
              />
            </div>
          )}

          <div>
            <Label className="text-secondary font-semibold">Informations complementaires</Label>
            <Textarea
              value={proposeData.information_complementaire}
              onChange={(e) => setProposeData({ ...proposeData, information_complementaire: e.target.value })}
              placeholder="Details sur l'evenement, attentes particulieres, contact..."
              className="border-[#E5E0DC] min-h-[100px]"
            />
          </div>

          {/* Responsable planning selection */}
          <div>
            <Label className="text-secondary font-semibold">Responsable du planning (optionnel)</Label>
            <select
              value={proposeData.responsable_planning_id}
              onChange={(e) => setProposeData({ ...proposeData, responsable_planning_id: e.target.value })}
              className="w-full mt-1 px-3 py-2 border border-[#E5E0DC] rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">-- Sélectionner un responsable --</option>
              {/* Option pour le proposeur de se désigner lui-même */}
              {isMembre && currentUserId && (
                <option value={currentUserId}>
                  Moi-même ({currentUserName || "Proposeur"})
                </option>
              )}
              {/* Membres du bureau */}
              {bureauMembers.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.display_name} ({member.email})
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground mt-1">
              {isMembre 
                ? "Vous pouvez vous désigner responsable ou choisir un membre du bureau"
                : "Membre du bureau qui suivra cet événement"
              }
            </p>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="proposeur_participe"
              checked={proposeData.proposeur_participe}
              onCheckedChange={(checked) => setProposeData({ ...proposeData, proposeur_participe: checked })}
            />
            <label htmlFor="proposeur_participe" className="text-sm cursor-pointer">
              Je souhaite participer a cet evenement avec mon food truck
            </label>
          </div>

          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              className="border-[#E5E0DC]"
            >
              Annuler
            </Button>
            <Button 
              type="submit" 
              disabled={submitting}
              className="bg-primary hover:bg-primary/90"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {isAdminOrBureau ? "Création et envoi..." : "Envoi en cours..."}
                </>
              ) : (
                isAdminOrBureau ? "Créer et envoyer aux food trucks" : "Envoyer la proposition"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
